/**
 * 앱 등록 «받아들이기» — 올리기 전에 meta 와 파일을 맞춰 본다 (2026-09-14).
 *
 * 왜 화면에서도 보나: 서버가 finalize 에서 크기 · sha256 을 다시 재지만, 그건 **200MB 를 다 올린 뒤**다.
 * `scripts/ops/publish-app.sh` 가 ① 단계에서 하는 것과 같은 검사를 여기서 먼저 한다 — 틀린 meta 를 골랐거나
 * 다른 빌드의 APK 를 집었을 때 올리기 전에 안다. 서버 검사(패키지 · 번호 증가 · 지문)는 서버 답을 그대로 보여 준다.
 *
 * 순수 함수만 둔다 — DOM · 네트워크 없음. 화면(AppReleaseSection)이 부른다.
 */
import type { AppReleaseMeta } from '../../../api/appReleases';

export interface MetaParseResult {
  meta: AppReleaseMeta | null;
  problems: string[];
}

const HEX64 = /^[0-9a-f]{64}$/;

/** apksigner · keytool 이 찍는 `AB:CD:…` · 공백 섞인 지문도 받는다(서버 normalizeHex64 와 같은 뜻). */
export function normalizeHex64(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const v = raw.replace(/[:\s]/g, '').toLowerCase();
  return HEX64.test(v) ? v : null;
}

/**
 * ⛔ **Gradle 이 만든 `output-metadata.json` 을 알아본다** (2026-09-24 · 사용자가 실제로 겪음).
 *
 * APK 옆에 있는 **유일한 JSON** 이 그것이라 사람이 그걸 고를 수밖에 없다. 그런데 칸 이름이 하나도
 * 안 맞아 **여섯 줄이 «다» 틀렸다고** 나왔고, 그것을 읽은 사람은 **「빌드가 망가졌나」**로 읽었다.
 * ⇒ 칸 이야기를 하지 말고 **「파일을 잘못 골랐다」**를 말한다.
 *
 * ## 무엇으로 알아보나
 *
 * 실측한 `output-metadata.json` 의 최상위 키: `version` · `artifactType` · `applicationId` ·
 * `variantName` · `elements` · `elementType` · `baselineProfiles` · `minSdkVersionForDexing`.
 * 그중 **`artifactType` · `elements`** 를 표식으로 쓴다 — 그 형식을 «정의하는» 칸이라 판이 바뀌어도
 * 남을 것이 가장 확실하다(나머지는 AGP 판에 따라 있고 없다).
 *
 * ⚠ 그리고 **우리 칸이 «하나라도» 있으면 이 길로 가지 않는다.** 진짜 app-meta 가 조금 상한 경우까지
 * 「파일을 잘못 골랐다」로 몰면 **고칠 곳을 못 찾게** 된다 — 그때는 아래 안내가 맞는 말이다.
 *
 * ⛔⛔ **여기에 «수»를 적지 않는다** (2026-10-07 정정). 종전에 「진짜 app-meta 의 칸은 … **여섯뿐**」
 *    이라고 적어 두었는데 **낡았다** — `#2251` 이 `gitCommit` 을 더해 **일곱**이 됐다.
 *    ⇒ ★ 수는 **그 수를 만든 곳**에서만 뜻이 있다. 그러니 여기서는 **정본을 가리킨다**:
 * ```
 * 정본 = aos/app/build.gradle.kts 의 WriteAppMetaTask (지금은 :203~210 이 그 칸들을 적는다)
 * ⇒ 「app-meta 가 내는 칸」은 ★ 거기서 읽는다 — 이 파일에 베껴 두면 또 낡는다
 * ```
 *
 * ⚠ 그리고 **아래 `APP_META_KEYS` 가 그 칸을 «다» 담을 필요가 없다.** 쓰임이
 *   `APP_META_KEYS.some((k) => k in obj)` — **「하나라도 있나」**이기 때문이다(표식이지 명세가 아니다).
 *   ⇒ ⛔ 그러니 「일곱인데 여섯만 있다」를 **결함으로 읽지 말 것.** 새 칸을 더하는 것은 자유지만
 *     **동작은 안 바뀐다**(그래서 그것을 시험으로 잠글 수도 없다).
 *
 * ⛔ **`applicationId` 를 `package` 로 «읽어 주지» 않는다.** 같은 뜻 다른 이름이라 솔깃해 보이지만,
 * 그 파일엔 `certSha256` · `sha256` · `size` 가 **애초에 없다** — 일부만 읽어 주면 「거의 되는 것처럼」
 * 보이다가 **더 나쁘게** 실패한다.
 */
const GRADLE_OUTPUT_MARKERS = ['artifactType', 'elements'] as const;
const APP_META_KEYS = ['package', 'versionName', 'versionCode', 'certSha256', 'sha256', 'size'] as const;

/** 빌드 산출물에서 «맞는» 파일이 있는 자리 — 안내가 이 경로를 그대로 말한다. */
export const APP_META_PATH_HINT = 'app/build/outputs/app-meta/<변이>/app-meta.json';

export const WRONG_FILE_OUTPUT_METADATA =
  '이 파일은 Gradle 이 만든 output-metadata.json 입니다 — 등록에 쓰는 것은 app-meta.json 입니다'
  + ` (${APP_META_PATH_HINT}).`;

/** 최상위만 본다 — Gradle 파일의 «정의 칸»이 있고 우리 칸은 하나도 없을 때. */
function looksLikeGradleOutputMetadata(obj: Record<string, unknown>): boolean {
  const hasMarker = GRADLE_OUTPUT_MARKERS.some((k) => k in obj);
  const hasOurs = APP_META_KEYS.some((k) => k in obj);
  return hasMarker && !hasOurs;
}

/** app-meta.json 본문(문자열) → meta. 칸이 빠지거나 모양이 틀리면 문제를 사람 말로 모은다. */
export function parseAppMeta(text: string): MetaParseResult {
  let obj: any;
  try { obj = JSON.parse(text); } catch { return { meta: null, problems: ['app-meta.json 을 읽을 수 없습니다 — JSON 이 아닙니다.'] }; }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return { meta: null, problems: ['app-meta.json 의 최상위가 객체가 아닙니다.'] };
  // ⛔ 그 파일이면 여기서 끝낸다 — 칸이 없는 것이 «당연»하므로 아래 여섯 줄은 잡음이다.
  if (looksLikeGradleOutputMetadata(obj)) return { meta: null, problems: [WRONG_FILE_OUTPUT_METADATA] };
  const problems: string[] = [];
  const pkg = typeof obj.package === 'string' ? obj.package.trim() : '';
  if (!pkg) problems.push('package 가 없습니다.');
  const versionName = typeof obj.versionName === 'string' ? obj.versionName.trim() : '';
  if (!versionName) problems.push('versionName 이 없습니다.');
  else if (versionName.length > 32) problems.push('versionName 이 32자를 넘습니다.');
  const versionCode = Number(obj.versionCode);
  if (!Number.isInteger(versionCode) || versionCode <= 0) problems.push('versionCode 는 양의 정수여야 합니다.');
  const certSha256 = normalizeHex64(obj.certSha256);
  if (!certSha256) problems.push('certSha256 이 64자리 16진수가 아닙니다.');
  const sha256 = normalizeHex64(obj.sha256);
  if (!sha256) problems.push('sha256 이 64자리 16진수가 아닙니다.');
  const size = Number(obj.size);
  if (!Number.isInteger(size) || size <= 0) problems.push('size 는 양의 정수(바이트)여야 합니다.');
  if (problems.length) return { meta: null, problems };
  return { meta: { package: pkg, versionName, versionCode, certSha256: certSha256!, sha256: sha256!, size }, problems: [] };
}

/** 300MB — 서버 `APP_RELEASE_MAX_BYTES` 와 같다. 서버가 400 을 주기 전에 화면이 먼저 말한다. */
export const APP_RELEASE_MAX_BYTES = 300 * 1024 * 1024;

/**
 * meta 와 고른 파일이 맞는가. `fileSha256` 은 화면이 잰 값(없으면 크기만 본다 — 재는 데 시간이 걸려 선택이다).
 * 문제가 없으면 빈 배열.
 */
export function checkFileAgainstMeta(
  meta: AppReleaseMeta,
  file: { name: string; size: number },
  fileSha256?: string | null,
): string[] {
  const problems: string[] = [];
  // 한도는 300×1024×1024 바이트(서버와 같음)라 1000 기준 표기로는 314.6 MB 다 — 「300MB 를 넘습니다 (310.0 MB)」 처럼
  //   숫자가 어긋나 보이지 않게 한도도 같은 단위로 함께 적는다(2026-09-14, #1335 에서 옮김).
  if (file.size > APP_RELEASE_MAX_BYTES) problems.push(`파일이 한도 300MB(${formatBytes(APP_RELEASE_MAX_BYTES)})를 넘습니다 (${formatBytes(file.size)}).`);
  if (meta.size !== file.size) problems.push(`크기가 다릅니다 — meta ${meta.size.toLocaleString()} ≠ 파일 ${file.size.toLocaleString()} 바이트. 같은 빌드의 APK 인지 확인하세요.`);
  if (fileSha256 && fileSha256 !== meta.sha256) problems.push('sha256 이 다릅니다 — meta 가 가리키는 파일이 아닙니다.');
  if (!/\.apk$/i.test(file.name)) problems.push('파일 이름이 .apk 로 끝나지 않습니다.');
  return problems;
}

/**
 * ★ 1000 기준 · 소수 한 자리 — 사용자 사이트 `formatAppSize`(#1327 관제 결정)와 같은 규칙. 안드로이드 앱이 1000 기준이라
 *   같은 APK 가 콘솔 205.2 MB · 앱 215.1 MB 로 달리 보이면 안 된다(리뷰-화면 #1332 지적). 한도 상수(300×1024²)는 서버와 같아 그대로다.
 */
export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '-';
  if (n < 1000) return `${n} B`;
  // 반올림한 뒤 가른다 — 999,999 B 가 「1000 KB」 가 아니라 「1.0 MB」 로.
  const mb = Math.round((n / 1_000_000) * 10) / 10;
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${Math.max(1, Math.round(n / 1000))} KB`;
}

/** 브라우저에서 sha256 을 잰다 — 200MB 는 몇 초 걸린다. `crypto.subtle` 이 없으면(비보안 컨텍스트) null. */
export async function sha256OfBlob(file: Blob): Promise<string | null> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) return null;
  const buf = await file.arrayBuffer();
  const digest = await subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}
